import { Component, Input, Output, EventEmitter, signal, ViewChild, computed, ElementRef, WritableSignal } from "@angular/core";
import { OriButton } from "~/components/ui/button";
import { CdkDrag, CdkDragHandle, CdkDropList, moveItemInArray, CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChevronDown, Settings2, Check, Search, GripVertical, Columns2, LucideAngularModule } from 'lucide-angular';
import { OriDropdownMenuTrigger, OriDropdownMenuContent, OriDropdownMenuSeparator, OriDropdownMenuItem } from '~/components/ui/dropdown-menu'; 
import { DropdownAlign } from '@radix-ng/primitives/dropdown-menu';
import { Table, Column } from '@tanstack/table-core';
import { FinDeNavCbemDto } from "~/src/app/Models/CBEM";
import { ColumnEditConfig } from "../configModel";
import { RdxDropdownMenuTriggerDirective } from '@radix-ng/primitives/dropdown-menu';


@Component ({
  selector: 'column-edit',
  imports: [
    OriButton,
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    LucideAngularModule,
    OriDropdownMenuTrigger,
    OriDropdownMenuContent,
    OriDropdownMenuSeparator,
  ],
  template: `
      <button
        #triggerBtn
        class="hover:bg-blue-50 shadow-md "
        [class]="config?.buttonLabelColor ? 'text-[' + config?.buttonLabelColor + '] rounded-full h-10 px-5 border-[#00358E] border text-[14px] font-medium' : ' rounded-full h-10 px-5 border-[#00358E] border text-[14px] font-medium'"
        [oriDropdownMenuTrigger]="menuContent"
        [align]="alignValue"
        [sideOffset]="4"
        [alignOffset]="alignOffsetValue"
        oriButton
        variant="outline"
      >
      @if(config?.buttonLabel){
        <img [src]="config?.buttonLabelIconPath" alt="Customize Icon" class="w-4 h-4 mr-1"/>
        {{ config?.buttonLabel }}
      }
      @else {
        <lucide-angular [img]="Columns2" size="16" strokeWidth="2"/>
        Edit Columns
        <lucide-angular [img]="ChevronDown" size="16" strokeWidth="2"/>
      }
      </button>

      <ng-template #menuContent>
        <div
          class="flex flex-col max-h-[50vh] w-80 rounded-sm overflow-auto py-0 pb-1"
          oriDropdownMenuContent
        >
          <div class="flex flex-col sticky top-0 z-10 bg-white">
            @if(config?.showSearch == false) {
              <div class="inline-flex py-2 mt-1 px-2 items-center justify-between bg-inherit border-none">
                <span class="font-medium text-sm text-[#00358E]">Customize View</span>

                <img (click)="closeDropdown()" src="assets/logos/Cross-mark-Client-Nav.svg" alt="Close Icon" class="w-4 h-4"/>
              </div>
            }
            @else{
            <div
              class="inline-flex gap-4 py-2 mt-1 px-2 items-center bg-inherit border-none"
            >
              <lucide-angular
                class="opacity-70" [img]="Search" size="16" strokeWidth="2" aria-hidden="true"/>

              <input #searchInput
                autofocus
                placeholder="Search column name.."
                class="flex-1 placeholder-gray-500 border-none text-sm shadow-none focus:outline-none appearance-none ring-0 focus-visible:outline-none"
                [value]="searchQuery()"
                (input)="onSearchInput($event)"
                type="text"/>
            </div>
            }
            <div oriDropdownMenuSeparator></div>
          </div>

          <div class="viewDrag-boundary"
            cdkDropList
            [cdkDropListData]="filteredColumns()"
            (cdkDropListDropped)="drop2($event)"
          >
          @if (filteredColumns().length == 0) {
            <div class="flex items-center justify-center h-[50px]">
              <span class="text-sm font-normal">No columns found.</span>
            </div>

          } @else {


            @for (col of filteredColumns(); track col.id;) {
              <div
                class="flex flex-row gap-1 items-center justify-start select-none"
                cdkDragLockAxis="y"
                cdkDragBoundary=".viewDrag-boundary"
                (cdkDragStarted)="dragStarted = true"
                (cdkDragEnded)="dragStarted = false"
                cdkDrag
              >
                <span
                  class="h-[26px] w-[26px] cursor-grab border-gray-100 shadow-none bg-white outline-none "
                  cdkDragHandle
                  oriButton
                  variant="noHover"
                  size="icon"
                >
                @if(config?.dragLabelIconPath){
                  <img [src]="config?.dragLabelIconPath" alt="Drag Icon" class="w-4 h-4"/>
                }
                @else{
                  <lucide-angular [img]="GripVertical" size="16" strokeWidth="2"/>
                }
                </span>
                <div
                  id="{{col.id}}"
                  role="checkbox"
                  [attr.aria-checked]="col.getIsVisible()"
                  (mousedown)="toggleColumnVisibility(col, $event)"
                  (mouseenter)="handleMouseEnter(col.id)"
                  [class.hover:bg-blue-100]="dragStarted == false"
                  class="truncate grow relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none"
                  
                >
                  <label class="font-normal truncate" oriLabel>{{col.columnDef.header}}</label>
                  <lucide-angular
                    class="opacity-80 ml-auto flex-shrink-0" 
                    [style]="config?.checkIcon ? 'color : ' + config?.checkIcon : ''"
                    [class.invisible]="!col.getIsVisible()"
                    [img]="Check" size="16" strokeWidth="2"/>
                </div>
              </div>
            }
          }
          </div>
        </div>
      </ng-template>


  `,
  standalone: true,
  styleUrl: './componentGlobal.scss'
})

export class EditColumnComponent {

  readonly ChevronDown = ChevronDown;
  readonly Search = Search;
  readonly Settings2 = Settings2;
  readonly DropdownAlign = DropdownAlign;
  readonly Check = Check;
  readonly GripVertical = GripVertical;
  readonly Columns2 = Columns2;
  readonly searchQuery = signal('');

  @Input() openFromButtonEnd = false;

  // Default dropdown alignment (used across the app)
  alignValue: DropdownAlign = DropdownAlign.End;
  alignOffsetValue = 0;


  readonly filteredColumns = computed<Column<FinDeNavCbemDto, unknown>[]>(() => {
    const tbl = this.table?.();
    if (!tbl) return [];

    const query = this.searchQuery().toLowerCase().trim();

    return tbl.getAllLeafColumns().filter(col => {
      // Skip select and action columns
      if (col.id === 'select' || col.id === 'action') {
        return false;
      }

      // If no search query, show all columns
      if (!query) {
        return true;
      }

      // Get header text for comparison
      const header = (col.columnDef.header as string)?.toLowerCase() ?? '';
      const columnId = col.id?.toLowerCase() ?? '';

      // Search in both header text and column ID
      return header.includes(query) || columnId.includes(query);
    });
  });

  dragStarted = false;
  lastHoveredField: string | null = null;

  @ViewChild('triggerBtn') triggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(RdxDropdownMenuTriggerDirective) dropdownTrigger!: RdxDropdownMenuTriggerDirective;
  @Input({ required: true}) table!: WritableSignal<Table<any>>;
  @Input() config?: ColumnEditConfig | null;
  @Output() tablePrefsChange = new EventEmitter<{ columnOrder: string[]; hiddenColumns: string[] }>();

  ngAfterViewInit(): void {
    // Only adjust alignment when explicitly requested by the caller.
    if (!this.openFromButtonEnd) {
      this.alignValue = this.DropdownAlign.End;
      this.alignOffsetValue = 0;
      return;
    }

    // Align dropdown so its LEFT edge starts at the trigger's RIGHT edge.
    // We achieve this by using Start alignment, then shifting by the trigger width.
    setTimeout(() => {
      const width = this.triggerBtn?.nativeElement?.offsetWidth ?? 0;
      this.alignValue = this.DropdownAlign.Start;
      this.alignOffsetValue = Math.max(width - 25, 0);
    });
  }

  // Handle search input with proper typing
  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchQuery.set(target.value);
  }

  closeDropdown() {
    if (this.dropdownTrigger) {
      this.dropdownTrigger.toggle();
    }
}


  drop2(event: CdkDragDrop<any[]>) {
    // Get only the filtered columns for reordering
    const filteredCols = this.filteredColumns();

    if (event.previousIndex === event.currentIndex) {
      return; // No change needed
    }

    // Create new order array from filtered columns
    const newOrder = [...filteredCols];
    moveItemInArray(newOrder, event.previousIndex, event.currentIndex);

    // Map to column IDs and apply to table
    const columnIds = newOrder.map(col => col.id);
    this.table().setColumnOrder(columnIds);
    this.emitTablePreferenceChange();
  }

  handleMouseEnter(field: string) {
    this.lastHoveredField = field;
  }

  toggleColumnVisibility(col: Column<FinDeNavCbemDto, unknown>, event: MouseEvent) {
    col.getToggleVisibilityHandler()(event);
    // Wait until TanStack state is updated before emitting the snapshot.
    queueMicrotask(() => this.emitTablePreferenceChange());
  }

  private emitTablePreferenceChange() {
    const tbl = this.table?.();
    if (!tbl) {
      return;
    }

    const leafColumns = tbl
      .getAllLeafColumns()
      .filter(col => col.id !== 'select' && col.id !== 'action');

    const hiddenColumns = leafColumns
      .filter(col => !col.getIsVisible())
      .map(col => col.id);

    const stateOrder = tbl.getState().columnOrder || [];
    const fallbackOrder = leafColumns.map(col => col.id);
    const columnOrder = stateOrder.length > 0 ? stateOrder : fallbackOrder;

    this.tablePrefsChange.emit({
      columnOrder,
      hiddenColumns,
    });
  }
}